import { tokenContract } from '@daymade/api-contracts';
import { GameType } from '@daymade/common-lib/dbEnums';
import { TRaffleDrawEntry } from '@daymade/common-lib/dbJsonModels';
import { ClientInferResponseBody } from '@ts-rest/core';
import MainLayout from 'components/layout/MainLayout';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import Separator from 'components/Separator';
import ALittleLostSection from 'components/token-town/a-little-lost-section';
import Header from 'components/token-town/header/Header';
import BackLink from 'components/token-town/links/BackLink';
import MinilottoTicket from 'components/token-town/MinilottoTicket';
import RaffleTicket from 'components/token-town/RaffleTicket';
import RetroIconSVG from 'components/token-town/RetroIconSVG';
import YouMightAlsoLikeSection from 'components/token-town/YouMightAlsoLikeSection';
import { MainDrawTypeCode } from 'constants/Draw';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { tokenApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import { whatNext } from '../../../components/token-town/whatNextContent';

export type TTokenRedemptionOptionsResponse = ClientInferResponseBody<
  typeof tokenContract.auth.getTokenRedemptionOrder,
  200
>;

const TokenTownOrderConfirmation = observer(() => {
  const router = useRouter();
  const { balances, currentUser, initialized } = useUserStore();

  const [orderDetails, setOrderDetails] =
    useState<TTokenRedemptionOptionsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    async function getConfirmation() {
      if (router.isReady && router.query && router.query.id && currentUser) {
        const res = await tokenApiClient.auth.getTokenRedemptionOrder({
          params: {
            // id is the tokenRedemptionId
            id: router.query.id as string,
          },
        });

        switch (res.status) {
          case HttpStatusCode.OK:
            setOrderDetails(res.body);
            setLoading(false);
            break;
          case HttpStatusCode.UNAUTHORIZED:
            return router.push('/token-town');
          case HttpStatusCode.BAD_REQUEST:
            setOrderDetails([]);
            setLoading(false);
            console.error(
              `Error fetching order (orderId: ${router.query.orderId}): ${res.body.message}`,
            );
            return {
              notFound: true,
            };
        }
      } else if (initialized) {
        return router.push('/token-town');
      }
    }

    getConfirmation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.id, currentUser]);

  const lottoEntryTypeConfirmationText = (
    entries: number[],
    bonuses: number[],
  ) => {
    if (entries.length === 1) {
      return `fixed chances draw.`;
    }

    if (entries.length && !bonuses.length) {
      return `mini lotto.`;
    }
  };

  let backTo: 'games' | 'prizes' = 'games';
  if (orderDetails && orderDetails.length) {
    const order = orderDetails[0];
    backTo =
      order.drawOrder?.drawTypeCode === MainDrawTypeCode ||
      (order.prizeOrder && order.prize && !order.drawOrder) ||
      order.treeLog
        ? 'prizes'
        : 'games';
  }

  return (
    <MainLayout>
      <div className="tokenTownOrderConfirmation fullWidth flexCentered">
        <Header />

        {orderDetails && orderDetails?.length <= 0 && (
          <h2>Oops! Looks like this order doesn't exist</h2>
        )}

        {orderDetails?.map((order) => {
          let confirmationResult;
          let orderType:
            | 'raffleEntry'
            | 'lottoEntry'
            | 'prize'
            | 'tree'
            | null = null;

          if (order.drawOrder) {
            if (order.drawOrder.entries[0].type === GameType.RAFFLE) {
              orderType = 'raffleEntry';
              const entries = order.drawOrder.entries;
              confirmationResult = (
                <>
                  <PaddedMaxWidthContainer additionalClassName="tokenConfirmation">
                    <RetroIconSVG
                      fname={`arcade-star-green`}
                      className="star-icon"
                    />

                    <h2 className="header">You're in!</h2>
                    <p className="orderConfirmationText">
                      You've successfully entered the '
                      <strong>{order?.prize?.title}</strong>' raffle.
                      <br />
                      Here's your{' '}
                      {entries && entries.length > 1 ? 'entries' : 'entry'}:
                    </p>

                    <div className="entriesContainer">
                      {entries?.map((entry, idx) => (
                        <RaffleTicket key={idx}>
                          {(entry as TRaffleDrawEntry).raffle}
                        </RaffleTicket>
                      ))}
                    </div>

                    <Separator className="tokenTownOrderConfirmationSelector" />
                  </PaddedMaxWidthContainer>
                </>
              );
            }

            if (order.drawOrder.entries[0].type === GameType.LOTTERY) {
              orderType = 'lottoEntry';
              const entries = order.drawOrder.entries;
              const main = order.drawOrder.entries[0].main;
              const bonuses = order.drawOrder.entries[0].bonus;
              confirmationResult = (
                <>
                  <PaddedMaxWidthContainer additionalClassName="tokenConfirmation">
                    <RetroIconSVG
                      fname={`arcade-star-green`}
                      className="star-icon"
                    />

                    <h2 className="header">You're in!</h2>
                    <p className="orderConfirmationText">
                      {order?.prize?.title ? (
                        <>
                          You've successfully entered the '
                          <strong>{order?.prize?.title}</strong>'{' '}
                          {lottoEntryTypeConfirmationText(main, bonuses)}
                        </>
                      ) : (
                        <>
                          You've successfully entered the weekly draw (
                          {order?.drawSchedule?.code}).
                        </>
                      )}
                      <br />
                      Here {main.length > 1 ? 'are' : 'is'} your lucky number
                      {main.length > 1 ? 's' : ''}:
                    </p>

                    {entries.map((entry, idx) => {
                      if (entry.type !== GameType.LOTTERY) return null;
                      return (
                        <div className="lottoContainer flexRow fullWidth">
                          {entry.main.map((number) => (
                            <MinilottoTicket key={`${idx}-main-${number}`}>
                              {number}
                            </MinilottoTicket>
                          ))}
                          {entry.bonus.length > 0 && (
                            <MinilottoTicket
                              key={`${idx}-bonus-${entry.bonus}`}
                              bonus={true}
                            >
                              {entry.bonus}
                            </MinilottoTicket>
                          )}
                        </div>
                      );
                    })}

                    <Separator className="tokenTownOrderConfirmationSelector" />
                  </PaddedMaxWidthContainer>
                </>
              );
            }
          }

          if (order.prizeOrder && order.prize && !order.drawOrder) {
            orderType = 'prize';
            confirmationResult = (
              <>
                <PaddedMaxWidthContainer additionalClassName="tokenConfirmation">
                  <RetroIconSVG
                    fname={`arcade-star-green`}
                    className="star-icon"
                  />

                  <h2 className="header">Success!</h2>
                  <p className="orderConfirmationText">
                    You’ve exchanged {order.order.tokenAmount} token
                    {order.order.tokenAmount === 1 ? '' : 's'} for our '
                    <strong>{order.prize.title}</strong>' prize.
                  </p>
                  <Separator className="tokenTownOrderConfirmationSelector" />
                </PaddedMaxWidthContainer>
              </>
            );
          }

          if (order.treeLog) {
            orderType = 'tree';
            confirmationResult = (
              <>
                <PaddedMaxWidthContainer additionalClassName="tokenConfirmation">
                  <RetroIconSVG
                    fname={`arcade-star-green`}
                    className="star-icon"
                  />

                  <h2 className="header">Success!</h2>
                  <p className="orderConfirmationText">
                    You’ve exchanged {order.order.tokenAmount} token
                    {order.order.tokenAmount === 1 ? '' : 's'} to
                    <strong>
                      {' '}
                      plant {order.treeLog.quantity} tree
                      {order.treeLog.quantity === 1 ? '' : 's'}
                    </strong>
                    .
                  </p>
                  <Separator className="tokenTownOrderConfirmationSelector" />
                </PaddedMaxWidthContainer>
              </>
            );
          }

          return (
            <>
              {confirmationResult}
              {orderType && (
                <PaddedMaxWidthContainer>
                  <h2 className="header">What next?</h2>
                  <div className="whatNextContainer">
                    {whatNext[orderType].content.map((item) => item)}
                  </div>
                  <h2 className="header endText">
                    {whatNext[orderType].endText}
                  </h2>
                  <Separator className="tokenTownOrderConfirmationSelector" />
                </PaddedMaxWidthContainer>
              )}
            </>
          );
        })}

        <BackLink to={backTo} />

        <YouMightAlsoLikeSection
          tokenBalance={balances?.tokenBalance ?? 0}
          backTo={backTo}
        />

        <ALittleLostSection />
      </div>
      <style jsx>{`
        .tokenTownOrderConfirmation {
          opacity: ${loading ? 0 : 1};
          transition: opacity 0.25s ease-in;
        }

        :global(.tokenTownOrderConfirmation .star-icon) {
          width: 55px;
        }

        :global(.tokenTownOrderConfirmation .trophy-icon) {
          width: 56px;
        }

        :global(.tokenTownOrderConfirmation .mail-icon) {
          width: 50px;
        }

        :global(.tokenTownOrderConfirmation .question-icon) {
          width: 50px;
        }

        :global(.tokenTownOrderConfirmation .tree-icon) {
          width: 50px;
        }

        :global(.whatNextContainer .retroIconSVG) {
          margin: 0 auto !important;
        }

        :global(.tokenTownOrderConfirmation .whatNextInfo .resultsLink) {
          text-decoration: underline !important;
          cursor: pointer;
        }

        :global(.tokenTownOrderConfirmation .whatNextHeader) {
          text-transform: none;
          letter-spacing: 0;
        }

        :global(.orderConfirmationText) {
          font-size: 1em;
          text-align: center;
          margin-bottom: 1em;
        }

        :global(.tokenTownOrderConfirmation .whatNextInfo) {
          align-self: start;
          max-width: 360px;
        }

        :global(.tokenTownOrderConfirmationSelector) {
          margin-top: 2rem !important;
        }

        :global(.tokenConfirmation) {
          margin-top: 3rem !important;
        }

        h2 {
          letter-spacing: 0;
        }

        .header {
          font-size: 1.2rem;
          font-family: 'Press Start 2P', 'Montserrat', sans-serif !important;
          margin-bottom: 2.2rem;
        }

        .endText {
          margin-bottom: 0.5rem;
          max-width: 690px;
        }

        .entriesContainer {
          margin: 1.5em 0 2em;
          display: grid;
          grid-template-columns: 98%;
          grid-gap: 1.5em;
        }

        .lottoContainer {
          display: flex;
          justify-content: center;
          margin: 1rem 0;
        }

        .whatNextContainer {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: auto auto auto;
          gap: 0rem 5rem;
          align-items: center;
        }

        @media (min-width: 700px) {
          :global(.tokenTownOrderConfirmation .star-icon) {
            width: 107px;
          }

          :global(.tokenTownOrderConfirmation .trophy-icon) {
            width: 83px;
          }

          :global(.tokenTownOrderConfirmation .mail-icon) {
            width: 77px;
          }

          :global(.tokenTownOrderConfirmation .tree-icon) {
            width: 73px;
          }

          :global(.tokenTownOrderConfirmation .question-icon) {
            width: 70px;
          }

          :global(.whatNextHeader) {
            font-size: 1.7em;
          }

          :global(.orderConfirmationText) {
            font-size: 1.5em;
            margin: 1em 0;
          }

          :global(.tokenTownOrderConfirmation .whatNextInfo) {
            margin: 0 0 1em;
          }

          :global(.tokenTownOrderConfirmationSelector) {
            margin-top: 3rem !important;
          }

          :global(.tokenConfirmation) {
            margin-top: 5rem !important;
          }

          .header {
            margin-top: 2rem;
            font-size: 1.7rem;
            margin-bottom: 1rem;
          }

          .entriesContainer {
            margin: 1.5em 0 4em;
            grid-template-columns: 48% 48%;
            grid-gap: 1.5em;
          }

          .lottoContainer {
            margin: 2rem 0;
          }

          .whatNextContainer {
            margin: 4em 0;
          }
        }
      `}</style>
    </MainLayout>
  );
});

export default TokenTownOrderConfirmation;
